<template>
  <div class="bonus-rule flex flex-col py-7 relative">

    <!-- Content of the rule -->
    <div class="flex flex-col sm:flex-row w-full relative">

      <!-- Priority setter -->
      <div v-if="bonusRulesCount>1" class="priority absolute align-center flex flex-col -left-5 text-center -top-1 h-12 justify-center">
        <fai v-if="isEditable"
             :icon="['far', 'chevron-up']"
             :class="{ disabled: bonusRule.priority === 0 }"
             @click="moveRuleUp"
             class="chevron pointer self-center"
        />
        <span class="position bg-blue h-5 rounded-full text-sm text-white w-5">{{ bonusRule.priority + 1 }}</span>
        <fai v-if="isEditable"
             :icon="['far', 'chevron-down']"
             :class="{ disabled: bonusRule.priority === bonusRulesCount-1 }"
             @click="moveRuleDown"
             class="chevron pointer self-center"
        />
      </div>

      <!-- Delete rule -->
      <a v-if="isEditable" class="absolute -right-8 text-gray-400 top-2" @click="$emit('deleteBonusRule', bonusRule)">&times;</a>

      <div class="flex-col flex-1">

        <bonus-engine-rule-condition
          :key="bonusRuleConditionsNotDisabled[0].unique_id"
          :condition.sync="bonusRuleConditionsNotDisabled[0]"
          :siblingConditions="siblingConditions[0]"
          :isEditable="isEditable"
          :eventTypes="eventTypes"
          :eventValues="eventValues[bonusRuleConditionsNotDisabled[0].event_type]"
          :eventValuesFilter="eventValuesFilter"
          :filteredEventValues="filteredEventValues"
          @update:eventValuesFilter="filter => $emit('update:eventValuesFilter', filter)"
          @deleteBonusRuleCondition="condition => $emit('deleteBonusRuleCondition', condition)"
          class="bonus-rule-condition"
        />
      </div>

      <div class="operator sm:pl-2 sm:mr-auto sm:my-0 relative" :class="{'my-6': bonusRuleConditionsNotDisabled.length > 1}">
        <select-dropdown
          variant="outlined"
          :values="[{value:'required', label:'and'}, {value:'optional', label:'or'}]"
          :selected="bonusRuleOperator"
          :disabled="!isEditable"
          :class="[bonusRuleConditionsNotDisabled.length > 1 ? 'visible' : 'invisible']"
          class="bonus-rule-operator text-blue"
          @update:selected="onOperatorChange"
        />
        <a v-if="isEditable && bonusRuleConditionsNotDisabled.length <= 1"
           class="text-xl absolute top-1 left-0 sm:left-8 whitespace-nowrap"
           @click="addCondition"
        >+ add element</a>
      </div>

      <div class="flex-col flex-1">
        <bonus-engine-rule-condition
          v-if="bonusRuleConditionsNotDisabled.length > 1"
          :key="bonusRuleConditionsNotDisabled[1].unique_id"
          :condition.sync="bonusRuleConditionsNotDisabled[1]"
          :siblingConditions="siblingConditions[1]"
          :isEditable="isEditable"
          :eventTypes="eventTypes"
          :eventValues="eventValues[bonusRuleConditionsNotDisabled[1].event_type]"
          :eventValuesFilter="eventValuesFilter"
          :filteredEventValues="filteredEventValues"
          @update:eventValuesFilter="(filter) => $emit('update:eventValuesFilter', filter)"
          @deleteBonusRuleCondition="(condition) => $emit('deleteBonusRuleCondition', condition)"
          class="bonus-rule-condition"
        />
      </div>

    </div>

    <!-- Points earned -->
    <div v-if="isEachConditionValid" class="reward flex flex-row items-center mt-4 self-start">
      <div :for="'bonus-points'+bonusRule.priority" class="self-center text-xl text-right">then</div>
      <div class="flex">
        <input
          type="number"
          :step="bonusPointsStep" :min="bonusPointsMin" max="100000"
          v-model="bonusRule.points"
          @keydown="preventNegativeNumber"
          :disabled="!isEditable"
          :id="'bonus-points'+bonusRule.priority"
          class="m-0 text-xl text-center box-content py-0 pr-0 w-20"
          :class="{'has-error': !isBonusPointsValid}"
        />
        <div :for="'bonus-points'+bonusRule.priority" class="pl-2 self-center text-xl">
          {{ parseFloat(bonusRule.points) | pluralize('point') }} per {{ programTypeEvent }}
        </div>
      </div>
    </div>


  </div>
</template>

<script>
import BonusEngineRuleCondition from "./BonusEngineRuleCondition";
import _ from "lodash";
import { SelectDropdown } from '@springbig/sb-components'

export default {
  name: "BonusEngineRule",
  components: {
    BonusEngineRuleCondition,
    SelectDropdown
  },
  props: {
    bonusRule: Object,
    bonusRulesCount: Number,
    isEditable: Boolean,
    eventTypes: Array,
    eventValues: Object,
    eventValuesFilter: Object,
    filteredEventValues: Object,
    programType: String
  },
  computed: {
    bonusRuleConditionsNotDisabled() {
      return this.bonusRule.conditions.filter(condition => !condition.disabled_at) ?? [];
    },
    bonusRuleOperator() {
      return this.bonusRuleConditionsNotDisabled[1]?.condition_type === 'optional' ? 'optional' : 'required';
    },
    programTypeEvent() {
      return this.programType === "points_per_dollar" ? "dollar" : "visit";
    },
    bonusPointsStep() {
      return this.programType === "points_per_dollar" ? 0.01 : 1;
    },
    bonusPointsMin() {
      return this.programType === "points_per_dollar" ? 0.01 : 1;
    },
    isBonusPointsValid() {
      if (this.programType === "points_per_dollar") {
        return this.isPointsPerDollarValid;
      } else {
        return this.isPointsPerVisitValid;
      }
    },
    isPointsPerDollarValid() {
      return this.bonusRule.points !== null
        && parseFloat(this.bonusRule.points) >= 0.01
        && parseFloat(this.bonusRule.points) <= 100000
        && Math.round(this.bonusRule.points * 100) / 100 === parseFloat(this.bonusRule.points)
        && !isNaN(parseFloat(this.bonusRule.points));
    },
    isPointsPerVisitValid() {
      return this.bonusRule.points !== null
        && parseFloat(this.bonusRule.points) >= 1
        && parseFloat(this.bonusRule.points) <= 100000
        && Number.isInteger(parseFloat(this.bonusRule.points))
        && !isNaN(parseFloat(this.bonusRule.points));
    },
    isEachConditionValid() {
      return !this.bonusRule.conditions.some(condition => condition.is_valid === false);
    },
    isRuleValid() {
      return this.isEachConditionValid && this.isBonusPointsValid;
    },
    siblingConditions() {
      return this.bonusRuleConditionsNotDisabled.map(condition =>
        this.bonusRuleConditionsNotDisabled.filter(c => condition.unique_id !== c.unique_id)
      );
    }
  },
  methods: {
    onOperatorChange(type) {
      this.setConditionsType(type);
      if (type === 'required'
        && this.bonusRuleConditionsNotDisabled[0].event_type === this.bonusRuleConditionsNotDisabled[1].event_type
      ) {
        for (const prop of ['event_type', 'event_value', 'event_value_label', 'event_range_min', 'event_range_max']) {
          this.bonusRuleConditionsNotDisabled[1][prop] = null;
        }
      }
    },
    setConditionsType(type) {
      this.bonusRuleConditionsNotDisabled.forEach(condition => {
        condition.condition_type = type;
      });
    },
    preventNegativeNumber(event) {
      if (event.key === '-') {
        event.preventDefault();
      }
    },
    moveRuleUp() {
      this.$emit('increasePriority', this.bonusRule);
    },
    moveRuleDown() {
      this.$emit('decreasePriority', this.bonusRule);
    },
    addCondition() {
      this.bonusRule.conditions.push(
        {
          unique_id: _.uniqueId(),
          condition_type: "required",
          disabled_at: null,
          event_type: null,
          event_value: null,
          event_range_min: null,
          event_range_max: null,
          is_valid: false
        }
      );
    }
  },
  watch: {
    isRuleValid(val) {
      this.bonusRule.is_valid = val;
    },
    bonusRuleConditionsNotDisabled: {
      deep: true,
      handler() {
        if (this.bonusRuleConditionsNotDisabled.length === 1) this.setConditionsType('required');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .condition, .reward {
  display: grid;
  grid-template-columns: 35px 1fr;
  gap: 15px 15px;
}

@media only screen and (min-width: 640px) {
  ::v-deep .condition {
    grid-template-columns: 35px 205px;
  }
}

.bonus-rule-condition::v-deep .dropdown-component.focused main.label-container {
  border-bottom: 0;
}

.bonus-rule-condition::v-deep .sb-content {
  border-top: 0;
}

.bonus-rule-condition::v-deep main.label-container, {
  border: 1px solid #128ff9;
  color: #128ff9;
  background-color: #fff;
  padding: 0.5rem 1rem;
  font-family: AileronLight, sans-serif;
  align-items: normal;
  height: 40px;
  white-space: nowrap;
  text-align: left;
  min-width: auto;
  box-shadow: none;
  &, .icon-container {
    font-size: 1rem;
    align-self: center;
  }
  > div {
    justify-content: space-between;
    .label-text {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.bonus-rule-operator::v-deep {
  min-width: 90px;
  .sb-label-container {
    height: 40px;
    font-family: AileronLight, sans-serif;
  }
}

.bonus-rule-condition::v-deep .dropdown-component {
  min-width: 200px;
  .sb-header {
    margin-top: -10px;
  }
  .sb-content-items {
    max-height: 305px;
  }
}

.sb-date::v-deep .label-container {
  white-space: nowrap;
  height: 40px;
  padding: .5rem;
  font-size: 1rem;
}

.sb-dropdown::v-deep {
  .sb-chevron-down, .sb-chevron-up {
    transition: none;
    font-size: 1rem;
  }
  .sb-label {
    font-size: 1rem;
  }
  .sb-rows-container {
    font-family: AileronLight, sans-serif;
  }
}

.dropdown-component.has-error::v-deep,
.sb-date.has-error::v-deep {
  .label-container {
    border-color: red;
    color: red;
  }
  .sb-content {
    border-color: red;
  }
}

.bonus-rule {
  @media only screen and (min-width: 640px) {
    width: 650px;
  }
  input {
    height: 40px;
    &.has-error {
      border-color: red;
      color: red;
    }
  }
}

.priority {
  > .chevron {
    color: #128ff9;
    cursor: pointer;
    &.disabled {
      color: #aaa;
      cursor: default;
    }
  }
}
</style>
